import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { ShieldCheck, ShieldAlert, ShieldX, Trash2, RefreshCw, Lock, Unlock, Mail } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  ok:    { color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: ShieldCheck, label: "Healthy" },
  warn:  { color: "bg-amber-100 text-amber-800 border-amber-300",     icon: ShieldAlert, label: "Needs attention" },
  error: { color: "bg-red-100 text-red-800 border-red-300",           icon: ShieldX,     label: "Broken — SEND BLOCKED" },
};

export default function EmailDiagnostics({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.get(`${API}/api/admin/diagnose-email-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load email diagnostics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const callAction = async (path, method = "POST", confirmText = null) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(path);
    try {
      const token = localStorage.getItem("token");
      await axios({
        method,
        url: `${API}${path}`,
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Done");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  if (user?.role !== "super_admin") return null;

  return (
    <Card className="card" data-testid="email-diagnostics-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Mail className="h-6 w-6 text-slate-700" />
            </div>
            <div>
              <CardTitle className="text-2xl" style={{ fontFamily: "Manrope" }}>Email Diagnostics</CardTitle>
              <CardDescription className="mt-1">
                Guardrail status for cross-location email leakage (super admin)
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="email-diag-refresh">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-slate-500">Loading…</p>}

        {data && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-4 gap-3">
              <SummaryTile label="Total" value={data.summary.total_locations} color="text-slate-700" />
              <SummaryTile label="Healthy" value={data.summary.ok} color="text-emerald-700" />
              <SummaryTile label="Warnings" value={data.summary.warn} color="text-amber-700" />
              <SummaryTile label="Errors (Blocked)" value={data.summary.error} color="text-red-700" />
            </div>

            {data.summary.error > 0 && (
              <Alert variant="destructive" data-testid="email-diag-error-alert">
                <ShieldX className="h-4 w-4" />
                <AlertTitle>Cross-location email sends are being BLOCKED</AlertTitle>
                <AlertDescription>
                  The guardrail detected a mismatch between the expected sender and the actual Gmail integration for
                  one or more locations. Lead emails will NOT go out until fixed.
                </AlertDescription>
              </Alert>
            )}

            {/* Per-location diagnostics */}
            <div className="space-y-3">
              {data.diagnostics.map((d) => {
                const meta = STATUS_META[d.status] || STATUS_META.warn;
                const Icon = meta.icon;
                const needsConfirm = d.issues.includes("EXPECTED_SENDER_UNCONFIRMED") && d.integrations.length === 1;
                const hasDupes = d.integrations.length > 1;
                const hasMismatch = d.issues.some((i) => i.startsWith("SENDER_MISMATCH"));
                return (
                  <div
                    key={d.location_id}
                    className={`border rounded-lg p-4 ${meta.color}`}
                    data-testid={`email-diag-loc-${d.location_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="h-4 w-4" />
                          <span className="font-semibold">{d.location_name}</span>
                          <Badge className={`text-xs ${meta.color}`}>{meta.label}</Badge>
                        </div>
                        <p className="text-xs opacity-70 font-mono">{d.location_id}</p>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className="text-xs uppercase opacity-60">Expected sender</div>
                            <div className="font-mono">{d.expected_sender_email || <em className="opacity-60">unconfirmed</em>}</div>
                          </div>
                          <div>
                            <div className="text-xs uppercase opacity-60">Gmail integrations ({d.integrations.length})</div>
                            {d.integrations.length === 0 && <div className="opacity-60">(none)</div>}
                            {d.integrations.map((i) => (
                              <div key={i.doc_id} className="flex items-center gap-2 font-mono text-xs">
                                <span>{i.gmail_email}</span>
                                {hasDupes && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      callAction(
                                        `/api/admin/email-config/integration/${i.doc_id}`,
                                        "DELETE",
                                        `Delete integration ${i.gmail_email}? This cannot be undone.`
                                      )
                                    }
                                    disabled={busy.includes(i.doc_id)}
                                    data-testid={`email-diag-delete-${i.doc_id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {d.issues.length > 0 && (
                          <div className="mt-3 text-xs">
                            <span className="font-semibold">Issues: </span>
                            {d.issues.join(", ")}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        {needsConfirm && (
                          <Button
                            size="sm"
                            onClick={() =>
                              callAction(
                                `/api/admin/email-config/confirm-sender/${d.location_id}`,
                                "POST",
                                `Confirm ${d.integrations[0].gmail_email} as the correct sender for ${d.location_name}? This unlocks lead email sends for this location.`
                              )
                            }
                            disabled={!!busy}
                            data-testid={`email-diag-confirm-${d.location_id}`}
                          >
                            <Lock className="h-3 w-3 mr-1" />
                            Confirm sender
                          </Button>
                        )}
                        {d.expected_sender_email && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              callAction(
                                `/api/admin/email-config/clear-sender/${d.location_id}`,
                                "POST",
                                `Clear the sender binding for ${d.location_name}? Emails will stop sending until re-confirmed.`
                              )
                            }
                            disabled={!!busy}
                            data-testid={`email-diag-clear-${d.location_id}`}
                          >
                            <Unlock className="h-3 w-3 mr-1" />
                            Clear
                          </Button>
                        )}
                        {hasMismatch && (
                          <span className="text-xs text-red-700 font-semibold">SENDS BLOCKED</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recent sends log */}
            {data.recent_sends?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold mb-2">Recent outbound emails (latest 25)</h4>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-left">
                      <tr>
                        <th className="p-2">When</th>
                        <th className="p-2">Type</th>
                        <th className="p-2">Location</th>
                        <th className="p-2">From</th>
                        <th className="p-2">To</th>
                        <th className="p-2">Status</th>
                        <th className="p-2">Trace</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_sends.map((s) => (
                        <tr key={s.trace_id} className="border-t">
                          <td className="p-2 font-mono whitespace-nowrap">{(s.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                          <td className="p-2">{s.email_type}</td>
                          <td className="p-2">{s.location_name || <em className="opacity-50">-</em>}</td>
                          <td className="p-2 font-mono">{s.gmail_from || <em className="opacity-50">blocked</em>}</td>
                          <td className="p-2 font-mono">{Array.isArray(s.recipients) ? s.recipients.join(", ") : ""}</td>
                          <td className="p-2">
                            {s.success ? (
                              <Badge className="bg-emerald-100 text-emerald-800">sent</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">{s.error || "failed"}</Badge>
                            )}
                          </td>
                          <td className="p-2 font-mono opacity-60">{(s.trace_id || "").slice(0, 8)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryTile({ label, value, color }) {
  return (
    <div className="border rounded-lg p-3 text-center bg-white">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}
